import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, MoveRight } from 'lucide-react';
import {
  TEMPLATES,
  HERO_DEMO_VIDEO_URL, HERO_DEMO_POSTER_URL,
  HERO_DEMO_VIDEO_2_URL, HERO_DEMO_POSTER_2_URL,
} from '../../utils/constants';
import { HERO_CAPTIONS } from '../../data/heroCaptions';
import { HERO_CAPTIONS_2 } from '../../data/heroCaptions2';
import { layoutCaption, normalizeCaptionStyle, transcriptWordRules } from '../../shared/captionLayout';
import { captionTextStyle, captionBoxGeometry, hexWithOpacity } from '../../shared/captionStyles';
import CaptionContent, { CaptionFreeWords } from '../common/CaptionContent';

/* Before/after hero: the SAME clip playing in sync on two phones — raw on the
   left, with live caption overlays on the right. The styles reference the actual
   TEMPLATES from constants.js, so the demo is honest — what the visitor sees on
   the right phone is exactly what the app produces. */
const DEMOS = [
  {
    id: 'viral',
    label: 'Viral & bold',
    video: HERO_DEMO_VIDEO_URL,
    poster: HERO_DEMO_POSTER_URL,
    vtt: '/hero-demo.vtt',
    captions: HERO_CAPTIONS,
    styleIds: ['desi-word', 'hormozi-style', 'mr-beast', 'tabahi-comic', 'top-up', 'stagger', 'pop-off'],
  },
  {
    id: 'clean',
    label: 'Clean & modern',
    video: HERO_DEMO_VIDEO_2_URL,
    poster: HERO_DEMO_POSTER_2_URL,
    vtt: '/hero-demo-2.vtt',
    captions: HERO_CAPTIONS_2,
    styleIds: ['editorial', 'handwritten', 'poster', 'typewriter', 'ali-abdaal', 'center-stage', 'liquid-glass', 'deep-glow', 'mota-thick'],
  },
];

/* The four latest templates. They lead their demo's chip row and carry a marigold dot, so the
   auto-tour opens on them and a returning visitor can see at a glance what is new. */
const NEW_STYLE_IDS = new Set(['editorial', 'handwritten', 'poster', 'typewriter']);
const hasNewStyles = (demo) => demo.styleIds.some((id) => NEW_STYLE_IDS.has(id));

const stylesFor = (demo) => demo.styleIds.map((id) => TEMPLATES.find((t) => t.id === id)).filter(Boolean);

/* One phone width that works everywhere: on phones/tablets, half the viewport
   minus the arrow + gaps + page padding (so two always fit side by side without
   horizontal scroll), never taller than ~52vh (28.5vh * 16/9 — hero stays above
   the fold on short laptops). From lg up the upload button sits beside the demo
   instead of under it, so the phones get the height back; the px caps are what
   two phones + the arrow can take from the hero's right-hand column. */
const PHONE_WIDTH_CLS = 'w-[min(calc(50vw-72px),28.5vh,250px)] sm:w-[min(calc(50vw-82px),28.5vh,250px)] md:w-[min(28.5vh,250px)] lg:w-[min(36vh,205px)] xl:w-[min(34vh,215px)]';

const HeroBeforeAfter = ({ cta }) => {
  const masterRef = useRef(null);   // right phone (captioned) — drives playback
  const rawRef = useRef(null);      // left phone — mirrors the master
  const containerRef = useRef(null);
  const previewIdxRef = useRef(-1);
  const activeWordRef = useRef(-1);
  const rafRef = useRef(null);
  const inViewRef = useRef(false);

  const railRef = useRef(null);
  const [demoIdx, setDemoIdx] = useState(0);
  const [styleIdx, setStyleIdx] = useState(0);
  // Auto-tour: cycle through the templates so visitors see every style without
  // touching anything. Stops for good once they tap a style chip themselves.
  const [autoTour, setAutoTour] = useState(true);
  const [previewIdx, setPreviewIdx] = useState(-1);
  const [activeWord, setActiveWord] = useState(-1);
  const [scale, setScale] = useState(0.5);
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const demo = DEMOS[demoIdx];
  const heroStyles = useMemo(() => stylesFor(demo), [demo]);
  // Normalized exactly as the editor normalizes burnStyles, so the shared layout and style helpers
  // see the defaults they expect instead of a bare template object.
  const style = useMemo(() => normalizeCaptionStyle(heroStyles[styleIdx]?.style || {}), [heroStyles, styleIdx]);
  const segs = demo.captions; // already in seconds, sorted
  // The REAL media size, not the phone's. textScaleFactor keys off the source aspect ratio and
  // every stagger template sets sizeBasis 'width', so a wrong value here silently mis-scales the
  // whole caption. Seeded with the demo clips' 9:16 until metadata arrives.
  const [videoDimensions, setVideoDimensions] = useState({ width: 1080, height: 1920 });

  /* Keep the style on air centred in the rail. scrollTo on the rail itself (rather than
     scrollIntoView) so a tour running below the fold never drags the page with it. */
  useEffect(() => {
    const rail = railRef.current;
    const chip = rail?.firstElementChild?.children[styleIdx];
    if (!rail || !chip) return;
    const railBox = rail.getBoundingClientRect();
    const chipBox = chip.getBoundingClientRect();
    rail.scrollTo({
      left: rail.scrollLeft + (chipBox.left - railBox.left) - (rail.clientWidth - chipBox.width) / 2,
      behavior: 'smooth',
    });
  }, [styleIdx, demoIdx]);

  /* ── Scale captions relative to a 1080-tall design space (matches the burn) ── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setScale((el.clientHeight / 1080) || 0.5);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ── Subtitle sync loop (binary search segment + active word) + raw-phone drift fix ── */
  useEffect(() => {
    const video = masterRef.current;
    if (!video) return;

    const sync = () => {
      const t = video.currentTime;

      // Keep the raw phone locked to the master.
      const raw = rawRef.current;
      if (raw && Math.abs(raw.currentTime - t) > 0.2) raw.currentTime = t;

      let index = -1;
      let low = 0;
      let high = segs.length - 1;
      while (low <= high) {
        const mid = (low + high) >>> 1;
        const s = segs[mid];
        if (t >= s.start && t <= s.end) { index = mid; break; }
        else if (t < s.start) high = mid - 1;
        else low = mid + 1;
      }
      // Gap-fill: hold the previous caption through silent gaps between segments.
      if (index === -1) {
        const prev = low - 1;
        const next = segs[prev + 1];
        if (prev >= 0 && next && t > segs[prev].end && t < next.start) index = prev;
      }

      if (index !== previewIdxRef.current) {
        previewIdxRef.current = index;
        setPreviewIdx(index);
      }
      if (index !== -1) {
        const words = segs[index].words;
        let w = -1;
        for (let i = 0; i < words.length; i++) {
          if (t >= words[i].start) w = i; else break;
        }
        if (w !== activeWordRef.current) {
          activeWordRef.current = w;
          setActiveWord(w);
        }
      }
    };

    const loop = () => {
      if (video.paused || video.ended) return;
      sync();
      rafRef.current = requestAnimationFrame(loop);
    };
    const onPlay = () => {
      setIsPlaying(true);
      rawRef.current?.play().catch(() => { });
      rafRef.current = requestAnimationFrame(loop);
    };
    const onPause = () => {
      setIsPlaying(false);
      rawRef.current?.pause();
      cancelAnimationFrame(rafRef.current);
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    sync();
    if (!video.paused && !video.ended) onPlay();

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      cancelAnimationFrame(rafRef.current);
    };
  }, [segs]);

  /* ── Template auto-tour: advance the style every few seconds while in view.
     After the last style of a clip, hop to the other clip and tour its styles,
     so both families get shown without any interaction. ── */
  useEffect(() => {
    if (!autoTour) return;
    // Depends on styleIdx/demoIdx so each beat re-arms with fresh values —
    // no state updates from inside another setter (React dev double-invokes
    // updaters, which made a nested setDemoIdx toggle cancel itself).
    const id = setInterval(() => {
      if (!inViewRef.current) return;
      if (styleIdx + 1 < heroStyles.length) {
        setStyleIdx(styleIdx + 1);
      } else {
        setDemoIdx((demoIdx + 1) % DEMOS.length);
        setStyleIdx(0);
      }
    }, 3200);
    return () => clearInterval(id);
  }, [autoTour, styleIdx, demoIdx, heroStyles.length]);

  /* ── Autoplay (muted) only while the demo is in view; pause when scrolled away ── */
  useEffect(() => {
    const video = masterRef.current;
    if (!video) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        inViewRef.current = entry.isIntersecting;
        if (entry.isIntersecting) video.play().catch(() => { });
        else video.pause();
      },
      { threshold: 0.4 }
    );
    io.observe(video);
    return () => io.disconnect();
  }, []);

  /* ── Reload + restart playback when the visitor switches clips ── */
  useEffect(() => {
    const video = masterRef.current;
    if (!video) return;
    previewIdxRef.current = -1;
    activeWordRef.current = -1;
    setPreviewIdx(-1);
    setActiveWord(-1);
    video.load();
    rawRef.current?.load();
    // play() straight after load() can reject (load aborts it) — retry once
    // the new source has data, so autoplay survives the mount/clip-hop race.
    const tryPlay = () => { if (inViewRef.current) video.play().catch(() => { }); };
    video.addEventListener('loadeddata', tryPlay);
    tryPlay();
    return () => video.removeEventListener('loadeddata', tryPlay);
  }, [demoIdx]);

  const togglePlay = () => {
    const v = masterRef.current;
    if (!v) return;
    v.paused ? v.play().catch(() => { }) : v.pause();
  };
  const toggleMute = () => {
    const v = masterRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const switchDemo = (i) => {
    setAutoTour(false); // visitor took control — stop hopping between clips
    if (i === demoIdx) return;
    setDemoIdx(i);
    setStyleIdx(0);
  };

  const seg = previewIdx >= 0 ? segs[previewIdx] : null;
  const isFreeLayout = style.layout === 'free';

  /* ── Shared caption layout: the very function the burn uses decides which words sit on which
     line, where a stagger staircase lands, and where Playful words are scattered. Hero and accent
     words are picked across the WHOLE transcript (as the burn does), so a caption's picks depend
     on its neighbours rather than on how many words it happens to hold. ── */
  const rulePicks = useMemo(
    () => (isFreeLayout ? null : transcriptWordRules(segs, style)),
    [segs, style, isFreeLayout],
  );
  const captionLayout = useMemo(() => {
    if (!seg || !seg.words || seg.words.length === 0) return null;
    return layoutCaption(seg, style, {
      videoWidth: videoDimensions.width,
      videoHeight: videoDimensions.height,
      hasDevanagari: false,
      rulePicks: rulePicks ? rulePicks[previewIdx] : null,
    });
  }, [seg, style, videoDimensions.width, videoDimensions.height, rulePicks, previewIdx]);

  const textStyle = useMemo(
    () => captionTextStyle(style, scale, { videoWidth: videoDimensions.width, videoHeight: videoDimensions.height }),
    [style, scale, videoDimensions.width, videoDimensions.height],
  );
  const geometry = useMemo(() => captionBoxGeometry(style, scale, { videoWidth: videoDimensions.width, videoHeight: videoDimensions.height }), [style, scale, videoDimensions.width, videoDimensions.height]);
  // Playful words reuse the caption's text styling (font, stroke, drop shadow); size, colour and
  // position are per word, so line-height and alignment are neutralised here.
  const freeWordStyle = useMemo(() => (isFreeLayout
    ? { ...textStyle, lineHeight: 1, whiteSpace: 'nowrap', textAlign: 'center', backdropFilter: 'none', WebkitBackdropFilter: 'none' }
    : null), [isFreeLayout, textStyle]);

  /* ── Shrink-to-fit: the phone here is far smaller than the 1080-wide burn
     canvas, so a line that fits in the real output can wrap in the preview.
     Karaoke captions render nowrap and get scaled down to fit one line,
     matching what the burned video actually looks like.

     Stagger is exempt: it renders an absolutely-positioned block with explicit px dimensions, so
     its scrollWidth says nothing about the staircase inside and dividing by it would squash a
     caption that already fits. The shared layout has placed those words to the frame already. ── */
  const captionBoxRef = useRef(null);
  const fitsItself = !!captionLayout?.stagger;
  useLayoutEffect(() => {
    const el = captionBoxRef.current; // plain wrapper — the blur-in animation runs
    if (!el || !el.parentElement) return; // on its child, so this transform sticks
    el.style.transform = 'none';
    if (fitsItself) return;
    const parent = el.parentElement;
    const cs = getComputedStyle(parent);
    const avail = parent.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const needed = el.scrollWidth;
    el.style.transformOrigin = 'center bottom';
    el.style.transform = needed > avail ? `scale(${(avail / needed).toFixed(3)})` : 'none';
  }, [previewIdx, styleIdx, demoIdx, scale, activeWord, fitsItself]);

  // Caption box background (boxMode / backgroundColor), excluding the "lines" box type.
  const hasBox = (style.boxMode || style.backgroundColor) && style.boxType !== 'lines';
  const boxStyle = useMemo(() => ({
    backgroundColor: hasBox ? (hexWithOpacity(style.backgroundColor, style.backgroundOpacity) || 'transparent') : 'transparent',
    padding: hasBox ? geometry.boxPaddingCss : 0,
    borderRadius: `${(style.borderRadius ?? 8) * scale}px`,
    ...textStyle,
  }), [hasBox, style, scale, geometry, textStyle]);

  /* The caption body comes from the shared renderer the editor uses, so the newer templates
     (stagger staircase, typewriter, hero/accent words) look here exactly as they do in the app
     and in the export. The hero used to carry its own cut-down version that only understood
     karaoke highlighting, which is why those templates could not be shown here before. */
  const renderCaption = () => (
    <CaptionContent
      segment={seg}
      segIndex={previewIdx}
      style={style}
      layout={captionLayout}
      activeWordIndex={activeWord}
      isPlaying={isPlaying}
      scale={scale}
      videoHeight={videoDimensions.height}
      geometry={geometry}
      getCurrentTime={() => masterRef.current?.currentTime || 0}
    />
  );

  /* aspect-[9/16] lives on the <video>, not this frame: the frame's 4px border
     would otherwise make the inner box narrower than 9:16, and object-cover
     would crop the video's left/right edges to compensate. */
  const phoneFrame = 'relative rounded-[1.75rem] overflow-hidden bg-black border-[4px] border-stone-900 dark:border-stone-700';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      className="relative flex flex-col items-center gap-6 w-full min-w-0"
    >
      {/* Before → After row */}
      <div className="flex items-center justify-center gap-3 sm:gap-6 md:gap-10 lg:gap-6 xl:gap-8 w-full">

        {/* Raw phone */}
        <div className="flex flex-col items-center gap-2.5">
          <div className={`${phoneFrame} ${PHONE_WIDTH_CLS}`}>
            <video
              ref={rawRef}
              src={demo.video}
              poster={demo.poster}
              className="block w-full aspect-[9/16] object-cover"
              muted
              loop
              playsInline
              preload="metadata"
              aria-hidden="true"
            >
              <track kind="captions" src={demo.vtt} srcLang="hi" label="Hinglish" />
            </video>
            <div className="absolute inset-0 bg-black/10" />
          </div>
          <span className="text-sm font-semibold font-jakarta text-stone-500 dark:text-stone-400">Your video</span>
        </div>

        {/* Arrow — nudges forward; the marigold disc is the brand's caption highlight */}
        <div className="flex flex-col items-center gap-1.5 shrink-0 px-0.5">
          <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-marigold-400 border-2 border-stone-900 flex items-center justify-center">
            <motion.span
              animate={{ x: [0, 3, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              className="flex"
            >
              <MoveRight className="w-5 h-5 sm:w-6 sm:h-6 text-stone-900" strokeWidth={2.5} />
            </motion.span>
          </div>
        </div>

        {/* Captioned phone */}
        <div className="flex flex-col items-center gap-2.5">
          <div ref={containerRef} className={`${phoneFrame} ${PHONE_WIDTH_CLS} shadow-[5px_5px_0_0_#4F46E5] sm:shadow-[7px_7px_0_0_#4F46E5] dark:shadow-[5px_5px_0_0_#818CF8] dark:sm:shadow-[7px_7px_0_0_#818CF8]`}>
            <video
              ref={masterRef}
              src={demo.video}
              poster={demo.poster}
              className="block w-full aspect-[9/16] object-cover"
              muted={muted}
              loop
              playsInline
              preload="metadata"
              onLoadedMetadata={(e) => {
                const { videoWidth: w, videoHeight: h } = e.currentTarget;
                if (w > 0 && h > 0) setVideoDimensions({ width: w, height: h });
              }}
            >
              <track kind="captions" src={demo.vtt} srcLang="hi" label="Hinglish" />
            </video>

            {/* Current template name — remounts on change so it pops in */}
            <motion.div
              key={`${demoIdx}-${styleIdx}`}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute top-2.5 left-2.5 z-20 hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-md border border-white/10"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-marigold-400" />
              <span className="text-[11px] font-semibold font-jakarta text-white/90">{heroStyles[styleIdx]?.name}</span>
            </motion.div>

            {/* Mute toggle */}
            <button
              onClick={toggleMute}
              aria-label={muted ? 'Unmute' : 'Mute'}
              className="tap-target absolute top-2.5 right-2.5 z-20 w-8 h-8 rounded-full bg-black/55 backdrop-blur-md border border-white/10 flex items-center justify-center text-white/90 hover:bg-black/70 transition-colors"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>

            {/* Caption overlay. Playful templates place every word across the whole frame, so
                they skip the caption box entirely and draw over the phone instead. */}
            {seg && isFreeLayout && (
              <div className="absolute inset-0 z-10 pointer-events-none">
                <CaptionFreeWords
                  segment={seg}
                  segIndex={previewIdx}
                  style={style}
                  layout={captionLayout}
                  activeWordIndex={activeWord}
                  scale={scale}
                  wordStyle={freeWordStyle}
                />
              </div>
            )}
            {seg && !isFreeLayout && (
              <div
                className="absolute inset-0 z-10 pointer-events-none flex justify-center"
                style={{
                  alignItems: style.position === 'top' ? 'flex-start' : style.position === 'middle' ? 'center' : 'flex-end',
                  // +70 lifts captions clear of the small phone's bottom edge —
                  // preview-only; the burn keeps the template's own marginV.
                  paddingBottom: style.position === 'bottom' ? `${((style.marginV ?? 90) + 70) * scale}px` : undefined,
                  paddingTop: style.position === 'top' ? `${(style.marginV ?? 90) * scale}px` : undefined,
                  paddingLeft: `${12 * scale * 2}px`,
                  paddingRight: `${12 * scale * 2}px`,
                }}
              >
                <div ref={captionBoxRef} className="inline-block">
                  {/* Stagger animates each word in on its own spoken time, so a blur-in on the
                      whole block would fight it. */}
                  <div
                    key={`${demoIdx}-${styleIdx}-${previewIdx}`}
                    className={`inline-block ${captionLayout?.stagger ? '' : 'animate-blur-in'}`}
                    style={boxStyle}
                  >
                    {renderCaption()}
                  </div>
                </div>
              </div>
            )}

            {/* Play/pause tap target */}
            <button
              onClick={togglePlay}
              aria-label={isPlaying ? 'Pause' : 'Play'}
              className="absolute inset-0 z-[15] flex items-center justify-center group focus:outline-none"
            >
              <span className={`w-12 h-12 rounded-full bg-black/35 backdrop-blur-sm flex items-center justify-center border border-white/15 transition-opacity duration-200 ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
                {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
              </span>
            </button>
          </div>
          <span className="text-sm font-bold font-jakarta text-indigo-600 dark:text-indigo-400">Ready to post</span>
        </div>
      </div>

      {/* CTA slot — sits between the demo and the style toys so the eye path is
          phone → arrow → phone → button */}
      {cta}

      {/* Clip + style switchers (just under the fold — the interactive toy). One tab row and one
          chip rail: wrapping nine chips made the card three rows taller on the second clip. */}
      <div className="w-full min-w-0 flex flex-col items-center gap-3">
        <div className="flex items-center gap-1.5 p-1 rounded-full bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
          {DEMOS.map((d, i) => (
            <button
              key={d.id}
              onClick={() => switchDemo(i)}
              className={`tap-target px-4 py-1.5 rounded-full text-xs font-bold font-jakarta transition-all duration-200 ${i === demoIdx ? 'bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 shadow-sm' : 'text-stone-600 dark:text-stone-300 hover:text-stone-800 dark:hover:text-stone-100'
                }`}
            >
              {d.label}
              {hasNewStyles(d) && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-marigold-400 align-middle" aria-hidden="true" />}
            </button>
          ))}
        </div>

        <div ref={railRef} className="landing-style-rail w-full min-w-0 max-w-full overflow-x-auto">
          <div role="group" aria-label="Caption style" className="flex w-max mx-auto gap-2 px-1 py-0.5">
            {heroStyles.map((t, i) => (
              <button
                key={t.id}
                onClick={() => { setAutoTour(false); setStyleIdx(i); }}
                aria-pressed={i === styleIdx}
                className={`tap-target shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold font-jakarta transition-all duration-200 border ${i === styleIdx
                  ? 'bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 border-stone-900 dark:border-stone-100'
                  : 'bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-300 border-stone-200 dark:border-stone-800 hover:border-stone-300 dark:hover:border-stone-700 hover:text-stone-900 dark:hover:text-stone-100'
                  }`}
              >
                {NEW_STYLE_IDS.has(t.id) && (
                  <>
                    <span className={`mr-1.5 inline-block w-1.5 h-1.5 rounded-full align-middle ${i === styleIdx ? 'bg-marigold-400' : 'bg-marigold-500'}`} aria-hidden="true" />
                    <span className="sr-only">New style: </span>
                  </>
                )}
                {t.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default HeroBeforeAfter;
